// Bundled UI state. One typed record instead of a pile of module-scope
// `let` flags in main.ts. Fields are read/written directly — typed so
// misspellings still error.

import type {
  ActionMode,
  Handedness,
  SelectAction,
  ShapeMode,
  TopMode,
} from "../interaction/tool";
import type { PaletteIndex } from "../model/voxel-grid";

export interface UIState {
  // Top-level mode toggled by the SELECT / SKETCH buttons on the
  // toolkit. Determines which joystick dials are active and what the
  // trigger does.
  topMode: TopMode;
  // What the trigger does in SKETCH mode (draw cells vs erase cells).
  // Set by the left-joystick action dial.
  actionMode: ActionMode;
  // How the trigger interprets a face press in SKETCH mode (single
  // voxel vs flood-fill extrude vs draw-a-rect box). Set by the right-
  // joystick shape dial.
  shapeMode: ShapeMode;
  // What the trigger does when it lands on a selected voxel in SELECT
  // mode. Set by the right-joystick select-action dial. (Trigger on
  // empty space / unselected voxel always starts a marquee instead.)
  selectAction: SelectAction;
  // Active palette index for DRAW (and DRAW-direction extrude / box).
  // Set by clicking a swatch on the palette panel.
  currentPaletteIndex: PaletteIndex;
  // Mirror-drawing axes. While an axis is on, every cell a sketch gesture
  // touches is also drawn at its reflection across the canvas center on that
  // axis (see interaction/mirror.ts). Independent toggles → 2/4/8-fold symmetry.
  symmetry: { x: boolean; y: boolean; z: boolean };
  // Mesher visualization toggles. Grid = cell-boundary lines on every
  // exposed face (MagicaVoxel default look). Edges = silhouette outline
  // where exposed faces of different orientations meet.
  gridLinesVisible: boolean;
  edgesVisible: boolean;
  // AR-only: when true, the immersive skybox is hidden so the user sees
  // real-world passthrough behind the cube. Toggled from the toolkit's
  // PASSTHRU button; no-op outside AR (button is inert).
  passthroughActive: boolean;
  // Which physical controller owns the "dominant" bindings (panel
  // summon face buttons + shape / select-action dial). The opposite
  // hand gets undo/redo + the action dial. Toggled from the SETTINGS
  // panel.
  dominantHand: Handedness;
  // Top-level app mode. "modeling" = the voxel editor; "floorplan" = the
  // continuous floor planner (walls + placed models). Desktop only for now.
  mode: "modeling" | "floorplan";
  // Floor-planner camera: "2d" top-down ortho plan view vs "3d" orbit.
  floorView: "2d" | "3d";
  // True while the VR layout walkthrough is running (written by app/vr-input's
  // enter/exitWalkthrough). The presentation lighting rig in main subscribes.
  walkthroughActive: boolean;
  // True while the AR layout tabletop is running (Layout-mode "VR edit" →
  // passthrough with the layout shrunk onto the desk in front of you). Written
  // by app/vr-input's enter/exitLayoutTabletop; the render loop branches on it.
  layoutTabletopActive: boolean;
  // Layout "Lighting" preview: render the plan with the walkthrough's
  // presentation lighting rig on the desktop (3D view only). Persisted.
  presentationLighting: boolean;
}

// Change notification. Writes go through setState(), which notifies that
// field's subscribers; reads stay direct (uiState.foo). Derived state that
// must track a field (laser tints, mesher visibility, toolbar highlights)
// subscribes once with onState() instead of being hand-called from every
// write site. Nested-object mutation (uiState.symmetry.x) does NOT notify —
// only whole-field writes do.
type StateListener = () => void;
const _listeners = new Map<keyof UIState, Set<StateListener>>();

export function setState<K extends keyof UIState>(
  key: K,
  value: UIState[K],
): void {
  if (uiState[key] === value) return;
  uiState[key] = value;
  const subs = _listeners.get(key);
  if (subs) for (const fn of subs) fn();
}

export function onState(key: keyof UIState, fn: StateListener): void {
  let subs = _listeners.get(key);
  if (!subs) _listeners.set(key, (subs = new Set()));
  subs.add(fn);
}

export const uiState: UIState = {
  topMode: "sketch",
  actionMode: "draw", // Paint by default (the canvas starts empty, not solid)
  shapeMode: "box",
  selectAction: "move",
  currentPaletteIndex: 10, // teal
  symmetry: { x: false, y: false, z: false },
  gridLinesVisible: true,
  edgesVisible: true,
  passthroughActive: true,
  dominantHand: "right",
  mode: "floorplan",
  floorView: "2d",
  walkthroughActive: false,
  layoutTabletopActive: false,
  presentationLighting: false,
};
